import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, KeyRound, Mail } from 'lucide-react';
import { useSession } from '../session';
import { type ApiError } from '../api';
import { BrandMark } from '../components/Brand';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Login() {
  const { role, login } = useSession();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'judge' | 'organizer'>('judge');
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (role) return <Navigate to={role === 'organizer' ? '/results' : '/'} replace />;

  async function signIn(e: FormEvent, credential: { email?: string; passcode?: string }, empty: string) {
    e.preventDefault();
    if (!credential.email?.trim() && !credential.passcode?.trim()) return setError(empty);
    setBusy(true);
    setError(null);
    try {
      const r = await login(credential);
      navigate(r === 'organizer' ? '/results' : '/', { replace: true });
    } catch (err) {
      setError((err as ApiError).message || 'Sign in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="force-light min-h-dvh bg-white md:grid md:grid-cols-2">
      {/* brand panel — desktop */}
      <div className="relative hidden bg-white md:block">
        <img
          src="/brand/aadpa-poster.png"
          alt="AADPA 9th Annual Conference — Embracing change for every mind"
          className="absolute inset-0 h-full w-full object-contain p-8"
        />
      </div>

      {/* form column */}
      <div className="flex min-h-dvh items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 flex items-center gap-3">
            <BrandMark className="size-12 ring-1 ring-border" />
            <div className="leading-tight">
              <h1 className="text-xl font-bold tracking-tight">Judge Scoring</h1>
              <p className="text-sm text-muted-foreground">AADPA 9th Annual Conference</p>
            </div>
          </div>

          <div className="rounded-2xl border bg-card p-6 shadow-sm">
            {mode === 'judge' ? (
              <form onSubmit={(e) => signIn(e, { email }, 'Please enter your email address.')} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Your email address</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className="pl-9"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(null); }}
                      disabled={busy}
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-sm font-medium text-destructive">{error}</p>}
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={busy}>
                  <LogIn />
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setMode('organizer'); setError(null); }}
                  className="block w-full text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Organiser sign-in
                </button>
              </form>
            ) : (
              <form onSubmit={(e) => signIn(e, { passcode }, 'Please enter the organiser passcode.')} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="passcode">Organiser passcode</Label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="passcode"
                      className="pl-9 text-center text-lg font-semibold tracking-[0.2em] placeholder:font-normal placeholder:tracking-normal"
                      autoComplete="off"
                      placeholder="Enter passcode"
                      value={passcode}
                      onChange={(e) => { setPasscode(e.target.value); setError(null); }}
                      disabled={busy}
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-sm font-medium text-destructive">{error}</p>}
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={busy}>
                  <LogIn />
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setMode('judge'); setError(null); }}
                  className="block w-full text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  I'm a judge
                </button>
              </form>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Trouble signing in? Contact the conference organisers.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
